import React from 'react'
import {formatDate} from '../../common/utils/dateUtils'
import {Box} from '../../webui/Box'
import {Container} from '../../webui/Container'
import {Paper} from '../../webui/Paper'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {Typography} from '../../webui/Typography'
import {useDashboardCounts} from '../box/gql/useDashboardCounts'
import {useAllRentals} from '../rentals/gql/useAllRentals'
import {RentalsPerDay} from '../statistic/rentals/RentalsPerDay'
import {useAllUsers} from '../user/gql/useAllUsers'
import {Dashboard} from './Dashboard'
import {RentalsThatMayNeedActionsTable} from './RentalsThatMayNeedActionsTable'
import {DASHBOARD_DAYS_BACK, dashboardInitialFromDate} from './dashboardUtils'
import {useRecentRentalsForDashboard} from '../rentals/gql/useRecentRentalsForDashboard'
import {computeDashboardMetrics} from './metrics/computeDashboardMetrics'
import {Rental} from '../../common/types/rental'

interface Props {}

export const StartPage: React.FC<Props> = () => {
  // Over-fetch a small surplus to be safe against server rounding
  const SURPLUS_DAYS = 2
  const fromDateSurplus = (() => {
    const [y, m, d] = dashboardInitialFromDate.split('-').map(Number)
    const dt = new Date(y, m - 1, d)
    dt.setDate(dt.getDate() - SURPLUS_DAYS)
    const yy = dt.getFullYear()
    const mm = String(dt.getMonth() + 1).padStart(2, '0')
    const dd = String(dt.getDate()).padStart(2, '0')
    return `${yy}-${mm}-${dd}`
  })()

  // Chart data: rentals in (slightly larger than) last N days
  const {data: recentData, loading: rentalsLoading, error: rentalsError} = useRecentRentalsForDashboard(fromDateSurplus)

  // Treat GraphQL nodes as full app Rentals at the boundary
  const fetchedRentals = (recentData?.rental?.allRentals?.nodes ?? []) as Rental[]

  // Centralized, midnight-stable metrics (also returns the clamped list)
  const metrics = computeDashboardMetrics<Rental>({
    rentals: fetchedRentals,
    daysBack: DASHBOARD_DAYS_BACK,
  })

  const rentals = metrics.windowedRentals

  // --------- caller-side warning if nothing was filtered out ----------
  if (fetchedRentals.length > 0 && rentals.length === fetchedRentals.length) {
    // Nothing was filtered → your SURPLUS_DAYS might be too small, or server window matches exactly.
    // This is a *warning* (not an error) to help you spot potential edge cases.
    // If you see this often, consider increasing SURPLUS_DAYS or verifying server-side windowing.
    console.warn('[dashboard] Window clamp did not remove any items.', {
      fetchedCount: fetchedRentals.length,
      clampedCount: rentals.length,
      surplusDays: SURPLUS_DAYS,
      fromKey: metrics._meta.fromKey,
      todayKey: metrics._meta.todayKey,
    })
  }
  // ------------------------------------------------------------------------

  // Totals: count-only fast paths (add cache-first + background refresh)
  const {
    data: {totalCount: totalAllRentalsCount},
    loading: allRentalsLoading,
    error: allRentalsError,
  } = useAllRentals({
    variables: {pagination: {offset: 0, limit: 0}},
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-and-network',
  })

  const {
    data: {totalCount: totalUsersCount},
    loading: usersLoading,
    error: usersError,
  } = useAllUsers({
    variables: {pagination: {offset: 0, limit: 0}},
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-and-network',
  })

  // Server-computed counts for boxes & unique products (same resilient policy)
  const {
    data: {activeBoxes, uniqueProductIds},
    loading: countsLoading,
    error: countsError,
  } = useDashboardCounts({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-and-network',
  })

  const hasRecentRentals = rentals.length > 0
  const hasTotals = totalAllRentalsCount !== undefined && totalUsersCount !== undefined && activeBoxes !== undefined && uniqueProductIds !== undefined

  const isColdStart = !hasRecentRentals && !hasTotals

  if (isColdStart && (allRentalsLoading || countsLoading || rentalsLoading || usersLoading)) {
    return <LoadingSpinnerContainer />
  }

  if (allRentalsError) return <div>{allRentalsError.message}</div>
  if (countsError) return <div>{countsError.message}</div>
  if (rentalsError) return <div>{rentalsError.message}</div>
  if (usersError) return <div>{usersError.message}</div>

  return (
    <>
      <Container>
        <Dashboard
          rentals={rentals}
          totalBoxesCount={activeBoxes}
          totalProductsCount={uniqueProductIds}
          totalRentalsCount={totalAllRentalsCount}
          totalUsersCount={totalUsersCount}
        />
        <Box mt={2} />
        <Paper>
          <Box p={2}>
            <Box m={2}>
              <Typography variant="h5">{`Number of rentals last ${DASHBOARD_DAYS_BACK} days`}</Typography>
            </Box>
            <RentalsPerDay
              fromDate={metrics._meta.fromKey} // exact local-window start
              endDate={formatDate(new Date())} // unchanged
              rentals={rentals} // clamped list
            />
          </Box>
        </Paper>
        <Box mt={2} />
      </Container>
      <Box>
        <Paper>
          <RentalsThatMayNeedActionsTable />
        </Paper>
      </Box>
    </>
  )
}
